import {
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  FileText,
  LoaderCircle,
  Minus,
  Plus,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { FeedbackBanner } from "@tribunalea/ui";
import { useApi } from "../App.jsx";

function clampPage(value, total) {
  return Math.max(1, Math.min(Number(value) || 1, total || 1));
}

export function EvidenceViewer({
  runId,
  evidence,
  documents = [],
  embedded = false,
  onClose,
  onSelectDocument,
}) {
  const api = useApi();
  const bodyRef = useRef(null);
  const canvasRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [page, setPage] = useState(evidence.page || 1);
  const [zoom, setZoom] = useState(1);
  const [renderVersion, setRenderVersion] = useState(0);
  const [state, setState] = useState({
    loading: true,
    rendering: false,
    error: "",
  });
  const evidencePage = evidence.page || null;
  const isEvidence = Boolean(evidence.quote || evidence.boxes?.length);

  useEffect(() => {
    let active = true;
    let loadedPdf;
    setPdf(null);
    setPage(evidence.page || 1);
    setZoom(1);
    setState({ loading: true, rendering: false, error: "" });

    Promise.all([
      import("pdfjs-dist"),
      import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
      api.getDocumentBlob(runId, evidence.documentId),
    ])
      .then(async ([
        { getDocument, GlobalWorkerOptions },
        workerModule,
        file,
      ]) => {
        GlobalWorkerOptions.workerSrc = `${workerModule.default}?module=1`;
        loadedPdf = await getDocument({
          data: new Uint8Array(await file.arrayBuffer()),
          isEvalSupported: false,
        }).promise;
        if (!active) {
          await loadedPdf.destroy();
          return;
        }
        setPage(clampPage(evidence.page, loadedPdf.numPages));
        setPdf(loadedPdf);
        setState({ loading: false, rendering: true, error: "" });
      })
      .catch((error) => {
        if (active) {
          setState({
            loading: false,
            rendering: false,
            error: error.message || "No pudimos abrir el PDF.",
          });
        }
      });

    const onKeyDown = (event) => {
      if (!embedded && event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      active = false;
      window.removeEventListener("keydown", onKeyDown);
      if (loadedPdf) void loadedPdf.destroy();
    };
  }, [api, embedded, evidence.documentId, onClose, runId]);

  useEffect(() => {
    if (!pdf || !evidence.page) return;
    setPage(clampPage(evidence.page, pdf.numPages));
  }, [evidence.page, pdf]);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body || !window.ResizeObserver) return undefined;
    let frame;
    const observer = new ResizeObserver(() => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        setRenderVersion((current) => current + 1);
      });
    });
    observer.observe(body);
    return () => {
      observer.disconnect();
      window.cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    if (!pdf || !canvasRef.current || !bodyRef.current) return undefined;
    let active = true;
    let renderTask;
    setState((current) => ({ ...current, rendering: true, error: "" }));

    pdf.getPage(page)
      .then((pdfPage) => {
        if (!active) return null;
        const baseViewport = pdfPage.getViewport({ scale: 1 });
        const availableWidth = Math.max(
          Math.min(bodyRef.current.clientWidth - 44, 1080),
          260,
        );
        const fitScale = availableWidth / baseViewport.width;
        const viewport = pdfPage.getViewport({ scale: fitScale * zoom });
        const canvas = canvasRef.current;
        const context = canvas.getContext("2d");
        const pixelRatio = window.devicePixelRatio || 1;
        canvas.width = Math.floor(viewport.width * pixelRatio);
        canvas.height = Math.floor(viewport.height * pixelRatio);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        renderTask = pdfPage.render({
          canvasContext: context,
          viewport,
          transform: pixelRatio === 1
            ? null
            : [pixelRatio, 0, 0, pixelRatio, 0, 0],
        });
        return renderTask.promise;
      })
      .then(() => {
        if (active) {
          setState((current) => ({ ...current, rendering: false }));
        }
      })
      .catch((error) => {
        if (!active || error?.name === "RenderingCancelledException") return;
        setState((current) => ({
          ...current,
          rendering: false,
          error: error.message || "No pudimos mostrar esta página.",
        }));
      });

    return () => {
      active = false;
      renderTask?.cancel();
    };
  }, [page, pdf, renderVersion, zoom]);

  const visibleBoxes = page === evidencePage ? evidence.boxes || [] : [];
  const primaryBox = visibleBoxes[0] || null;

  useEffect(() => {
    if (
      state.loading
      || state.rendering
      || !primaryBox
      || !bodyRef.current
      || !canvasRef.current
    ) {
      return undefined;
    }
    const frame = window.requestAnimationFrame(() => {
      const body = bodyRef.current;
      const canvas = canvasRef.current;
      const pageElement = canvas?.parentElement;
      if (!body || !canvas || !pageElement) return;
      const top = pageElement.offsetTop
        + primaryBox.y * canvas.clientHeight
        - body.clientHeight / 2;
      const left = pageElement.offsetLeft
        + primaryBox.x * canvas.clientWidth
        - body.clientWidth / 2;
      body.scrollTo({
        top: Math.max(0, top),
        left: Math.max(0, left),
        behavior: "smooth",
      });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [
    evidence.documentId,
    evidencePage,
    primaryBox?.height,
    primaryBox?.width,
    primaryBox?.x,
    primaryBox?.y,
    state.loading,
    state.rendering,
  ]);

  const viewer = (
    <>
        <header>
          {embedded ? (
            <div
              className="run-pdf-tabs"
              role="tablist"
              aria-label="Documentos procesados"
            >
              {documents.map((document) => (
                <button
                  type="button"
                  role="tab"
                  aria-selected={document.id === evidence.documentId}
                  className={
                    document.id === evidence.documentId ? "is-active" : ""
                  }
                  key={document.id}
                  title={document.fileName}
                  onClick={() => onSelectDocument?.(document)}
                >
                  <FileText size={14} />
                  <span>{document.fileName}</span>
                </button>
              ))}
            </div>
          ) : (
            <div>
              <span className="eyebrow">
                {isEvidence
                  ? `EVIDENCIA · PÁGINA ${evidencePage}`
                  : "PDF DE ENTRADA"}
              </span>
              <strong>{evidence.fileName}</strong>
            </div>
          )}
          <div>
            <button
              className={embedded ? "icon-button" : "secondary-button"}
              title="Descargar PDF"
              onClick={() => api.downloadDocument(
                runId,
                evidence.documentId,
              )}
            >
              <Download size={16} />
              {!embedded && <span>Descargar</span>}
            </button>
            <button
              className={embedded ? "icon-button" : "secondary-button"}
              title="Abrir PDF en otra pestaña"
              onClick={() => api.openDocument(
                runId,
                evidence.documentId,
                page,
              )}
            >
              <ExternalLink size={16} />
              {!embedded && <span>Abrir PDF</span>}
            </button>
            {!embedded && (
              <button
                className="icon-button"
                onClick={onClose}
                aria-label="Cerrar"
              >
                <X size={20} />
              </button>
            )}
          </div>
        </header>

        <div className="evidence-pdf-toolbar">
          <button
            type="button"
            title="Página anterior"
            disabled={!pdf || page <= 1}
            onClick={() => setPage((current) => current - 1)}
          >
            <ChevronLeft size={16} />
          </button>
          <span>
            <strong>{page}</strong>
            <i>/</i>
            {pdf?.numPages || "—"}
          </span>
          <button
            type="button"
            title="Página siguiente"
            disabled={!pdf || page >= pdf.numPages}
            onClick={() => setPage((current) => current + 1)}
          >
            <ChevronRight size={16} />
          </button>
          <em />
          <button
            type="button"
            title="Alejar"
            disabled={!pdf || zoom <= 0.6}
            onClick={() => setZoom((current) =>
              Math.max(0.6, Number((current - 0.2).toFixed(1))))}
          >
            <Minus size={15} />
          </button>
          <span>{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            title="Acercar"
            disabled={!pdf || zoom >= 2}
            onClick={() => setZoom((current) =>
              Math.min(2, Number((current + 0.2).toFixed(1))))}
          >
            <Plus size={15} />
          </button>
          {isEvidence && page !== evidencePage && (
            <button
              className="evidence-return-button"
              type="button"
              onClick={() => setPage(evidencePage)}
            >
              Volver a la evidencia
            </button>
          )}
        </div>

        <div className="evidence-document" ref={bodyRef}>
          {(state.loading || state.rendering) && !state.error && (
            <div className="evidence-loading">
              <LoaderCircle className="spin" size={22} />
              {state.loading ? "Preparando el PDF…" : "Mostrando la página…"}
            </div>
          )}
          {state.error && (
            <div className="evidence-error">
              <FeedbackBanner>{state.error}</FeedbackBanner>
              <button
                className="secondary-button"
                onClick={() => api.openDocument(
                  runId,
                  evidence.documentId,
                  page,
                )}
              >
                <FileText size={16} /> Abrir en otra pestaña
              </button>
            </div>
          )}
          <div className="pdf-page" hidden={state.loading || state.error}>
            <canvas ref={canvasRef} />
            {visibleBoxes.map((box, index) => (
              <span
                className="evidence-highlight"
                key={`${box.x}-${box.y}-${index}`}
                style={{
                  left: `${box.x * 100}%`,
                  top: `${box.y * 100}%`,
                  width: `${box.width * 100}%`,
                  height: `${Math.max(box.height, 0.012) * 100}%`,
                }}
              />
            ))}
          </div>
        </div>
        {isEvidence && (
          <footer>
            <span>Fragmento identificado</span>
            <q>{evidence.quote}</q>
          </footer>
        )}
    </>
  );

  if (embedded) {
    return (
      <aside className="run-pdf-preview" aria-label="Vista del documento">
        {viewer}
      </aside>
    );
  }

  return (
    <div className="evidence-modal" role="dialog" aria-modal="true">
      <button
        className="evidence-modal-backdrop"
        onClick={onClose}
        aria-label="Cerrar documento"
      />
      <div className="evidence-modal-panel">
        {viewer}
      </div>
    </div>
  );
}
